<template>
  <div
    v-if="product"
    v-click-outside="closeMonitor"
    :class="
      ['product-card',
       'product-card--has-manufacturer',
       { 'product-card--is-expanded': isExpanded(product.id) },
       { 'product-card--is-compact': useCompressedView },
       { 'product-card--has-fractions': hasFractions }]">
    <div v-if="!useListView" class="product-card__media-left">
      <oie-base-checkbox
        v-if="showCompareProducts"
        is-for="product-card"
        :text="tStatic('compareProducts')"
        size="small"
        text-color="gray"
        css-classes="product-card__compare"></oie-base-checkbox>

      <oie-base-label v-if="product.newsSection" is-type="new" :text="product.newsSection"></oie-base-label>
      <oie-base-label v-if="product.section" is-type="info" :text="product.section"></oie-base-label>
      <oie-base-label
        v-if="product.isUniversal"
        is-type="universal"
        :text="tStatic('universalLabel')"
        :tooltip-text="tStatic('universalLabelTooltip')"></oie-base-label>
      <oie-base-label
        v-if="showFitsLabel(product)"
        is-type="info"
        :text="tStatic('fitsLabel')"
        :tooltip-text="tStatic('fitsLabelTooltip')"></oie-base-label>
      <oie-base-label v-if="campaignTag" is-type="campaign" :text="campaignTag"></oie-base-label>
    </div>

    <div class="product-card__media">
      <a :href="getUrlWithVehicle(product)" class="product-card__url">
        <oie-base-image
          :images="product.imageUrl ? `${getAssetPath(product.imageUrl)}?preset=ProductSmall&watermark=OIEWatermarkMedium` : `${getAssetPath('/globalassets/missing404/404.png')}?preset=ProductSmall`"
          :alt-text="stripHtml(product.name)"
          :image-width="productImageSize"
          :image-height="productImageSize"
          class="product-card__image"></oie-base-image>
      </a>

      <oie-base-image
        v-if="useListView && product.productBrand"
        :images="manufacturerLogo(product.productBrand)"
        :alt-text="product.productBrand"
        class="product-card__manufacturer-logo"></oie-base-image>
    </div>

    <div :class="['product-card__header', { 'product-card__header--no-labels': !product.newsSection && !product.section && !product.isUniversal && !campaignTag && !showFitsLabel(product)}]">
      <oie-base-image
        v-if="!useListView && product.productBrand"
        :images="getAssetPath(manufacturerLogo(product.productBrand))"
        :alt-text="product.productBrand"
        class="product-card__manufacturer-logo"></oie-base-image>

      <div v-if="!useListView && !product.productBrand" class="product-card__manufacturer-logo"></div>

      <div v-if="useListView" class="product-card__header-tags">
        <oie-base-label v-if="product.newsSection" is-type="new" :text="product.newsSection"></oie-base-label>
        <oie-base-label v-if="product.section" is-type="info" :text="product.section"></oie-base-label>
        <oie-base-label
          v-if="product.isUniversal"
          is-type="universal"
          :text="tStatic('universalLabel')"
          :tooltip-text="tStatic('universalLabelTooltip')"></oie-base-label>
        <oie-base-label
          v-if="showFitsLabel(product)"
          is-type="info"
          :text="tStatic('fitsLabel')"
          :tooltip-text="tStatic('fitsLabelTooltip')"></oie-base-label>
        <oie-base-label v-if="campaignTag" is-type="campaign" :text="campaignTag"></oie-base-label>
      </div>

      <h5 class="product-card__name">
        <a :href="getUrlWithVehicle(product)" class="product-card__url" v-html="product.name"></a>
      </h5>

      <component
        :is="isSingleProduct ? 'a' : 'div'"
        :href="isSingleProduct ? getUrlWithVehicle(product) : null"
        class="product-card__article-number product-card__url">
        <template v-if="isSingleProduct">
          {{ tStatic('articleNumberLabel') }}&nbsp;<span v-html="product.items[0].idHtml || product.items[0].id"></span>
        </template>
      </component>

      <component
        :is="isSingleProduct ? 'a' : 'div'"
        v-if="isHtml(product.items[0].referenceNumber)"
        :href="isSingleProduct ? getUrlWithVehicle(product) : null"
        class="product-card__reference-number product-card__url">
        <template v-if="isSingleProduct && product.items[0].referenceNumber">
          {{ tStatic('referenceNumberLabel') }}&nbsp;<span v-html="product.items[0].referenceNumber"></span>
        </template>
      </component>

      <button v-if="(useListView || mq('to-small') ) && Object.keys(product.specification).length > 0 && (typeOfListing=='Product listing' || typeOfListing=='Search listing')" class="product-card__technical-toggler" @click="toggleActiveTechnicalInformationBlock(product.id)">
        <template v-if="showTechnicalnformation || isTechnicalInformationBlockActive(product.id)">
          {{ tStatic('filterBarTitleHideTechInfo') }}
        </template>

        <template v-else>
          {{ tStatic('filterBarTitleShowTechInfo') }}
        </template>
      </button>
    </div>

    <p
      v-if="!useCompressedView"
      :class="[
        'product-card__descriptions',
        { 'product-card__descriptions--is-empty': product.description === null && product.contextDescription === '' },
        { 'product-card__descriptions--no-clamp': product.description === null && product.contextDescription !== '' }
      ]">
      <span>
        <template v-if="!product.currentVehicle">
          <div v-html="product.description"></div>
        </template>

        <template v-if="product.contextDescription">

          <span v-if="getSelectedFractionTypeContexts(product.contextDescriptionDictionary).length > 0" class="product-card__context-description-bold">{{ tStatic('contextDescriptionLabel') }}</span>
          <div v-for="(context, key) in getSelectedFractionTypeContexts(product.contextDescriptionDictionary)" :id="key" :key="key">
            <span v-html="context"></span>
          </div>
        </template>
      </span>
    </p>

    <div class="product-card__actions">
      <div v-if="!isQuoteItem" class="product-card__price-status">
        <oie-product-status
          v-if="isSingleProduct && !useCompressedView"
          :status="getStockStatus(product.items[0])"
          :text="getStockMessage(product.items[0])"
          css-classes="product-card__status">
        </oie-product-status>

        <oie-product-price
          :current-object="product"
          :is-product="true"
          class="product-card__price"></oie-product-price>
      </div>
      <div
        v-else
        class="caption product-card__quote-info"
        v-html="tStatic('requestQuoteContent')"></div>

      <p v-if="hasFractions && !hasCompleteFraction && showFractionFilter" class="product-card__fractions-information" v-html="tStatic('fractionsAttentionText')"></p>

      <div v-if="!useCompressedView" class="product-card__information-action">
        <template v-if="isSingleProduct">
          <oie-request-quote-slide-in v-if="isQuoteItem" :item-id="product.items[0].id"></oie-request-quote-slide-in>

          <oie-base-action
            v-else
            v-element-loader="{ endpoint: 'addTocart' }"
            stretch
            theme="secondary"
            size="large"
            :disabled="hasNoPrice || !enableAddToCartButton(product.items[0])"
            @click="addItemToCart(product.items[0].id)">
            {{ tStatic('addToCartButtonLabel') }}
          </oie-base-action>

          <oie-add-to-shopping-list-slide-in :product="product"></oie-add-to-shopping-list-slide-in>

          <oie-notify-me-slide-in
            v-if="showNotifyMe"
            :article-number="product.items[0].id"
            :article-name="`${product.name} ${currentVehicle ? ', ' + currentVehicle.brand + ' ' + currentVehicle.displayName : ''}`">
          </oie-notify-me-slide-in>
        </template>

        <template v-else>
          <oie-base-action
            size="large"
            stretch
            :theme="isExpanded(product.id) ? 'outlined-white' : 'primary'"
            @click="toggleVariants(product.id)">
            {{ isExpanded(product.id) ? tStatic('showLessArticles') : tStatic('showArticles') }}
          </oie-base-action>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
// eslint-disable-next-line
import _ from 'lodash'
import fractionService from '@/core/fraction-service'
import gtm from '@/core/enhanced-ecommerce-gtm'
import utils from '@/core/utils'
import { productStatuses } from '@/variables/product-statuses'

export default {
  props: {
    showFractionFilter: { type: Boolean, required: false, default: false },
    showFilter: { type: Boolean, required: false, default: false },
    index: {
      type: Number,
      default: undefined
    },
    product: {
      type: Object,
      required: true
    },
    useCompressedView: {
      type: Boolean,
      required: false,
      default: false
    },
    typeOfListing: {
      type: String,
      default: ''
    },
    isSearchPage: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    expanded: [],
    showNotificationForm: false
  }),
  computed: {
    ...mapGetters('ui', ['showCompareProducts', 'showTechnicalnformation', 'useListView', 'isTechnicalInformationBlockActive']),
    ...mapGetters(['enableGtm', 'enableArticleWatch', 'currentVehicle', 'longDeliveryTimeLeadTime']),
    ...mapGetters('productVariants', ['isExpanded']),
    hasCompleteFraction() {
      return this.showFilter && fractionService.hasSelectedMatchingFractions(this.product.id).matchesOneOfAll
    },
    hasFractions() {
      return this.showFilter && fractionService.getItemFractions(this.product.id)
    },
    hasNoPrice() {
      const item = this.product.items[0]
      return item.customerPrice === null || item.customerPrice === undefined || item.customerPrice === 0
    },
    isQuoteItem() {
      return this.isSingleProduct && this.product.items[0].publicationStatusCode == productStatuses.noPrice
    },
    getRouteCode() {
      let uri = window.location.search.substring(1)
      let params = new URLSearchParams(uri)

      if(params.get('category') !== null) {
        return params.get('category')
      }

      return ''
    },
    productImageSize() {
      return this.useListView ? '82px' : '130px'
    },
    hasSelectedItems() {
      return this.product.items.filter((item) => item.selected).length > 0
    },
    campaignTag () {
      return this.product.items[0] && this.product.items[0].customerPrice && this.product.items[0].customerPrice.campaignTag ? this.product.items[0].customerPrice.campaignTag : ''
    },
    showNotifyMe() {
      return this.enableArticleWatch && this.isSingleProduct && this.getStockStatus(this.product.items[0]) === 'no-stock'
    },
    isSingleProduct() {
      return this.product.items.length === 1
    }
  },
  mounted() {
    this.subscribe('toggle.articles.all', (open) => {
      if(this.isSingleProduct) {
        return
      }

      if((open && !this.isExpanded(this.product.id)) || (!open && this.isExpanded(this.product.id))) {
        this.toggleVariants(this.product.id)
      }
    })
  },
  methods: {
    ...mapActions('commerce', ['addToCart']),
    ...mapActions('ui', ['toggleActiveTechnicalInformationBlock']),
    ...mapActions('productVariants', ['toggleVariants']),
    getSelectedFractionTypes(key, context) {
      return fractionService.getSelectedFractionTypesForItem(key, context, this.product.id)
    },
    getSelectedFractionTypeContexts(activeContexts) {
      return fractionService.getSelectedFractionTypesForItemProductListing(activeContexts, this.product.id)
    },
    getUrlWithVehicle(product) {
      if(product.currentVehicle == null) {
        return this.getUrlBasedOnLocation(product)
      }
      const selectedFractions = utils.getQueryStringParam('f')
      const selectedFractionsQueryParam = selectedFractions ? `&f=${encodeURIComponent(selectedFractions)}` : ''

      if(product.currentVehicle !== this.getRouteCode) {
        return this.getUrlBasedOnLocation(product) + '?currentVehicle=' + product.currentVehicle + '&category=' + this.getRouteCode + selectedFractionsQueryParam
      }

      return this.getUrlBasedOnLocation(product) + '?currentVehicle=' + product.currentVehicle + selectedFractionsQueryParam
    },
    getUrlBasedOnLocation(product) {
      const productListing = 'Product listing'
      if(this.typeOfListing !== productListing && this.isSingleProduct) {
        return product.items[0].url
      }
      if(this.typeOfListing !== productListing) {
        return product.url
      }

      var pathArray = window.location.pathname.split('/')
      var newPathname = ''
      for (var i = 3; i <= pathArray.length - 1; i++) {
        newPathname += '/'
        newPathname += pathArray[i]
      }
      if(this.isSingleProduct) {
        return `/${this.clientModel.currentMarket}/${this.clientModel.currentLanguage}/product${newPathname}${product.items[0].id}/${product.id}`
      }
      return `/${this.clientModel.currentMarket}/${this.clientModel.currentLanguage}/product${newPathname}${product.id}`
    },
    getStockStatus(item) {
      var number = item.stock
      var threshold = this.getProductFewInStock()
      if (number === undefined) { number = 0 }
      if (threshold === undefined) { threshold = 0 }
      if (number >= threshold) {
        return 'in-stock'
      } else if (number > 0) {
        return 'few-stock'
      } else if (item.isOrderItem) {
        return 'is-order-item'
      } else {
        return 'no-stock'
      }
    },
    getStockMessage(item) {
      var number = item.stock
      if (number === undefined) {
        number = 0
      }

      var threshold = this.getProductFewInStock()
      if (threshold === undefined) {
        threshold = 0
      }

      if (number >= threshold) {
        return this.tStatic('stockAvailableLabel')
      } else if (number > 0) {
        return this.tStatic('stockFewLabel')
      } else if (item.isOrderItem) {
        return this.tStatic('stockIsOrderItemLabel')
      } else if (!this.enableAddToCartButton(item)) {
        return this.tStatic('longDeliveryTime')
      } else {
        return this.tStatic('stockNoneLabel')
      }
    },
    manufacturerLogo(manufacturer) {
      if (manufacturer === '' || manufacturer == null) {
        return
      }

      return `/globalassets/brands/${manufacturer}.jpg`
    },
    addItemToCart(itemId) {
      this.addToCart({ itemId, productId: this.product.id, purchasedOnCategory: this.product.analyticsCategory, purchasedToVehicle: this.clientModel.analyticsVehicle }).then((data) => {
        if (this.enableGtm && data.cart) {
          const addedProduct = data.cart.rows.find(x => (x.itemId === itemId) && (x.productId === this.product.id))

          const gtmObject = gtm.createGtmItem(
            addedProduct.itemId,
            addedProduct.displayName,
            addedProduct.item.purchasedToVehicle,
            addedProduct.item.purchasedOnCategory,
            this.formatPriceWithoutCurrency(addedProduct.price),
            1,
            this.typeOfListing)

          gtm.addToCart(gtmObject)
        }
      })
    },
    toggleMonitor() {
      this.showNotificationForm = !this.showNotificationForm
    },
    closeMonitor() {
      this.showNotificationForm = false
    },
    showFitsLabel(product) {
      return product.fractions && product.fractions.length === 0 && !product.isUniversal
    },
    isHtml(item) {
      const htmlRegex = /<\/?[a-z][\s\S]*>/i
      return htmlRegex.test(item)
    },
    stripHtml: function(html) {
      return html.replace(/<\/?[^>]+(>|$)/g, '')
    },
    enableAddToCartButton(item) {
      if (this.getStockStatus(item) === 'no-stock' && item.deliveryDate && item.deliveryDate.startsWith('2030-01-01') && item.deliveryTime >= this.longDeliveryTimeLeadTime) {
        return false
      }

      return true
    }
  }
}
</script>

<style lang="scss">
    .product-card {
      position: relative;
      display: flex;
      flex-direction: column;
      background-color: color(background-white);
      border: 1px solid color(border-gray);
    }

    .product-card--has-fractions {
      border-bottom: none;
    }

    .grid--list .product-card {
      flex-direction: row;
      min-height: 8.25rem;
      height: auto;
    }

    .product-card--is-expanded {
      border-color: color(border-brand-blue);
    }

    .grid--list .product-card--is-expanded {
      border-color: color(border-gray);
    }

    .grid--list .product-card > *:not(:last-child) {
      border-right: 1px solid color(border-gray);
    }

    .product-card__media {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 10rem;
      padding: measure(half);
    }

    .grid--list .product-card__media {
      flex-basis: 19.375rem;
      min-height: 9rem;
      height: auto;
    }

    .product-card__image {
      width: 8.125rem;
      height: 8.125rem;
    }

    .grid--list .product-card__image {
      width: 5.125rem;
      height: 5.125rem;
    }

    .product-card__media-left {
      position: relative;
      margin-top: measure(quarter);
      margin-left: measure(quarter);
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      min-height: 1.875rem;
      z-index: 1;
    }

    .product-card__media-left > * {
      margin-bottom: measure(eighth);
    }

    .product-card__manufacturer-logo {
      position: absolute;
      top: measure(quarter);
      right: measure(half);
      max-height: 1.1rem;
      width: auto;
    }

    .grid--grid .product-card__manufacturer-logo {
      position: relative;
      top: 0;
      right: 0;
      margin-bottom: measure(eighth);
    }

    .product-card__header {
      padding: measure(eighth) measure(half);
    }

    .grid--list .product-card__header {
      position: relative;
      flex-basis: 16.875rem;
      padding: measure(half);
      padding-top: 3rem;
      border-right: none !important;
    }

    .grid--list .product-card__header--no-labels {
      padding-top: measure(half);
    }

    .product-card__header-tags {
      position: absolute;
      top: measure(quarter);
      left: measure(half);
      display: flex;
    }

    .product-card__header-tags > *:first-child {
      margin-right: measure(eighth);
    }

    .product-card__name {
      @include body-bold;
      // height: 3.125rem;
      margin: 0;
      padding: 0;
    }

    .product-card__url {
      display: block;
      color: color(text);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    .grid--list .product-card__name {
      height: auto;
      margin-bottom: measure(eighth);
    }

    .product-card__article-number, .product-card__reference-number {
      @include caption-semi-bold;
      display: flex;
      align-items: center;
      // height: 1.25rem;
      height: auto;
      color: color(text-blue);
    }

    .product-card__technical-toggler {
      @include reset-button;
      @include body-smaller;
      display: flex;
      align-items: center;
      margin-top: measure(quarter);
      text-decoration: underline;
      overflow: hidden;

      &::before {
        @include background-svg(infoCircle);
        content: '';
        display: inline-block;
        width: 1rem;
        height: 1rem;
        margin-right: measure(quarter);
        background-size: contain;
        background-repeat: no-repeat;
      }
    }

    .product-card__descriptions {
      @include body-smaller;
      position: relative;
      margin: 0;
      padding: measure(eighth) measure(half) measure(quarter) measure(half);
      word-break: break-word;
      overflow: hidden;
      white-space: pre-line;

      @include media(target-ie) {
        &:after {
          content: '';
          position: absolute;
          right: 0;
          bottom: 0;
          left: 0;
          height: 4rem;
          background-image: linear-gradient(to bottom, transparent, #ffffff);
        }
      }
    }

    .product-card__context-description-bold {
      @include body-smaller-semi-bold;
      display: block;
    }

    .grid--grid .product-card__descriptions {
      @include media(target-ie) {
        height: 5.6875rem !important;
      }
    }

    .product-card__descriptions--is-empty {
      height: auto;
      padding: 0;
    }

    .grid--list .product-card__descriptions {
      flex-basis: 16.875rem;
      flex-grow: 1;
      padding: measure(half);
      height: auto;

      @include media(target-ie) {
        height: 10rem;
      }
    }

    .product-card__descriptions > span {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .product-card__descriptions > span {
      -webkit-line-clamp: 4;
    }

    .grid--list .product-card__descriptions > span {
      -webkit-line-clamp: 7;
    }

    .product-card__descriptions.product-card__descriptions--no-clamp > span,
    .grid--list .product-card__descriptions.product-card__descriptions--no-clamp > span {
      -webkit-line-clamp: initial;
    }

    .product-card__actions {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      padding: measure(half);
      border-top: 1px solid color(border-gray);
      position: relative;
      gap: measure(quarter);
      justify-content: space-between;
    }

    .product-card--is-compact .product-card__actions {
      padding: measure(half);
    }

    .grid--list .product-card__actions {
      flex-direction: row;
      flex-basis: 28.125rem;
      flex-shrink: 0;
      align-items: flex-end;
      padding: measure(half);
      border-top: none;
    }

    .product-card__price-status {
      position: relative;
      display: flex;
      flex-direction: column;
      padding-top: measure(eighth);
      margin-bottom: measure(quarter);
      padding-right: measure(quarter);
    }

    .product-card__price {
      margin-top: auto;
    }

    .product-card--is-compact .product-card__price-status {
      height: auto;
      margin-top: 0;
      margin-bottom: 0;
    }

    .grid--list .product-card__price-status {
      align-items: flex-start;
      flex-grow: 1;
      height: 100%;
      padding-top: 0;
      margin-bottom: 0;
    }

    .grid--grid .product-card__price-status {
      flex-grow: 1;
    }

    .product-card__status {
      margin-bottom: measure(quarter);
    }

    .product-card__fractions-information {
      @include caption;
      margin-top: 0;
    }

    .product-card__information-action {
      display: flex;
      align-items: center;
      gap: measure(quarter);
      white-space: nowrap;
    }

    .product-card__information-action > *:first-child {
      flex-grow: 1;
    }

    .product-card__notification-form {
      position: absolute;
      z-index: 11;
      width: calc(100vw - 3.125rem);
      max-width: 21.8rem;

      @include media(to-medium) {
        position: fixed;
        top: 0;
        right: 0;
        left: 0;
        bottom: 0;
        margin: 0 !important;
        width: unset;
        max-width: unset;
        z-index: 99;
        border: none;
      }

    }

    .grid--grid .product-card__notification-form {
      margin: measure(quarter) 0 0 0;
    }

    .grid--list .product-card__notification-form {
      margin: measure(eighth) 0 0 0;
      right: 0;
    }

    .grid--list .product-card__actions .button {
      flex-basis: auto;
    }

    .grid--list .product-card__information-action {
      margin-left: auto;
    }

    .product-card__notify-button {
      width: 100%;
    }

    .product-card em {
        background-color: #44B1FA;
        font-style: normal;
        color: white;
        padding: 0rem .1rem 0rem .05rem;
    }

    .product-card em.instant-result__complete_match {
        background-color: #006db6;
        padding: .15rem .4rem .15rem .4rem;
        border-radius: 2px;
    }

    .product-card__long-delivery-time {
      border: 1px solid #B14932;
      padding: .4rem;
      margin: 0 0 .5rem 0;
    }
</style>
